// Third-Party
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

// Internal
import { useDispatch } from "@redux/store";
import { setOdooConnection } from "@redux/slices/askDostChat";
import { connectErp } from "../services/askDostService";
import { extractSyncedEntities, extractSyncErrors } from "../utils/utils";
import { ISyncedEntity } from "../types";

interface IUseOdooConnectFormProps {
  translate: (text: string, options?: any) => string;
  onClose: () => void;
  onConnectStart?: (retry: () => void) => void;
  onConnectResult?: (
    entities: ISyncedEntity[] | null,
    errorKeys?: string[],
  ) => void;
}

export interface IOdooConnectFormValues {
  odooUrl: string;
  username: string;
  apiKey: string;
  dbName: string;
}

export const useOdooConnectForm = ({
  translate,
  onClose,
  onConnectStart,
  onConnectResult,
}: IUseOdooConnectFormProps) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const odooConnectSchema = Yup.object().shape({
    odooUrl: Yup.string()
      .trim()
      .url(translate("invalid_url"))
      .required(translate("odoo_url_required")),
    username: Yup.string().trim().required(translate("username_required")),
    apiKey: Yup.string().trim().required(translate("api_key_required")),
    dbName: Yup.string().trim(),
  });

  const methods = useForm<IOdooConnectFormValues>({
    defaultValues: { odooUrl: "", username: "", apiKey: "", dbName: "" },
    resolver: yupResolver(odooConnectSchema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const performConnect = async (values: IOdooConnectFormValues) => {
    try {
      const payload = {
        provider: "odoo",
        url: values.odooUrl,
        db: values.dbName || "",
        username: values.username,
        api_key: values.apiKey,
        sync: true,
      };

      const res = await connectErp(payload);

      if (res && res.ok) {
        const data = await res.json().catch(() => null);
        const entities = extractSyncedEntities(data, translate);
        const syncErrors = extractSyncErrors(data);

        const errorKeys = data?.last_sync?.errors
          ? Object.keys(data.last_sync.errors)
          : [];

        dispatch(
          setOdooConnection({
            host: values.dbName
              ? `${values.odooUrl} · ${values.dbName}`
              : values.odooUrl,
            user: values.username,
            db: values.dbName || undefined,
            syncedAt: translate("just_now"),
            syncedEntities: entities,
          }),
        );
        onConnectResult?.(entities, errorKeys);
        syncErrors.forEach((err) => {
          const lowerErr = err.toLowerCase();

          const displayErr = lowerErr.includes("purchase.order")
            ? translate("odoo_missing_po_model_error")
            : err;

          enqueueSnackbar(displayErr, {
            variant: "warning",
            autoHideDuration: 7000,
          });
        });
      } else {
        enqueueSnackbar(translate("erp_connection_failed"), {
          variant: "error",
        });
        onConnectResult?.(null);
      }
    } catch (error) {
      console.error("ERP Connect Error:", error);
      enqueueSnackbar(translate("erp_connection_failed"), { variant: "error" });
      onConnectResult?.(null);
    }
  };

  const submitOdooConnection = async (values: IOdooConnectFormValues) => {
    handleClose();
    onConnectStart?.(() => performConnect(values));
    await performConnect(values);
  };

  return {
    methods,
    isSubmitting,
    handleClose,
    handleConnect: handleSubmit(submitOdooConnection),
  };
};
